import config from 'config';
import { fetchData } from 'framework/api/fetch-data';
import { toastSuccess, toastWarning } from '../../../../stores/toast';
import { i18n } from 'i18n';
import { SubmitResult } from '../../../components/ui/FormComponents/Form/Form.interface';
import { DeleteFilesResponse, FileFolder, FileFolderType, MoveFilesResponse, MultiFileUpload } from './types';
import { MAX_FILES_UPLOAD_SIZE } from './constants';

// fetch folders or fetch subfolders if includeFiles flag is true
export const fetchFolders = async (path?: string, includeFiles?: boolean) => {
  const pathSegment = path ? `/${path}` : '';
  return fetchData<FileFolder[]>({
    url: `${config.apiEndpoints.cms}/dam${pathSegment}`,
    query: {
      type: includeFiles ? undefined : 'directory',
    },
  });
};

export const searchFiles = async (searchTerm: string) => {
  const result = await fetchData<FileFolder[]>({
    url: `${config.apiEndpoints.cms}/dam/search`,
    method: 'PUT',
    body: {
      searchTerm,
    },
  });

  return result;
};

export const deleteFileFolder = async (fileFolderReference: string) =>
  fetchData({
    url: `${config.apiEndpoints.cms}/dam/${fileFolderReference}`,
    method: 'DELETE',
  });

export const deleteMultipleFiles = async (files: string[]) =>
  fetchData<DeleteFilesResponse>({
    url: `${config.apiEndpoints.cms}/dam/batch-delete`,
    method: 'POST',
    body: {
      filePathsToDelete: files,
    },
  });

// Move multiple files to a new location using the batch-move endpoint
export const moveMultipleFiles = async (destinationPath: string, files: string[], overwrite: boolean = false) =>
  fetchData<MoveFilesResponse>({
    url: `${config.apiEndpoints.cms}/dam/batch-move/${destinationPath}`,
    method: 'POST',
    query: {
      overwrite,
    },
    body: {
      filePathsToMove: files,
    },
  });

export const formatChildren = (folders?: FileFolder[]) =>
  folders
    ? folders.map((f) => ({
        ...f,
        children: f.type === FileFolderType.DIRECTORY ? f.children ?? [] : undefined,
      }))
    : [];

// after folders have been fetched, attach them as sub-children to currently highlighted element
// If includeSubFolders is true, keep previously attached children
export const attachChildren = (
  reference: string,
  folders: FileFolder[],
  attach: FileFolder[],
  parent?: FileFolder,
  includeSubFolders?: boolean
) =>
  folders.map((f) => ({
    ...f,
    parent,
    children:
      f.reference === reference
        ? [...(includeSubFolders && f.children ? f.children : []), ...attach]
        : [...attachChildren(reference, f.children ?? [], attach, f, includeSubFolders)],
  }));

export const selectUploadEndpoint = (uploadableFiles: File[], destinationFolder: FileFolder): string => {
  return uploadableFiles.length > 1
    ? `${config.apiEndpoints.cms}/dam/batch-upload/${destinationFolder.reference}`
    : `${config.apiEndpoints.cms}/dam/${destinationFolder.reference}`;
};

export const handleMultiFileResponse = (res: SubmitResult<MultiFileUpload>): void => {
  if (!res.data) return;

  if (res.data.successCount > 0) {
    const files: string = res.data.successfulUploads.map((file) => file.item.name).toString();
    toastSuccess(i18n.t('modules.contentmanagement.filesUploaded', { files }));
  }

  if (res.data.failureCount > 0) {
    const files: string = res.data.failedUploads.map((file) => file.fileName).toString();
    toastWarning(i18n.t('modules.contentmanagement.filesFailedUpload', { files }), 50000);
  }
};

export const checkFilesSizePass = (files: File[]): boolean => {
  // Max 100MB
  const filesSize: number = files.reduce((pFile, cFile) => {
    return pFile + cFile.size;
  }, 0);

  return filesSize <= MAX_FILES_UPLOAD_SIZE;
};

export const mapSearchResults = (data: FileFolder[], results: FileFolder[]) => {
  let finalData: FileFolder[] = [...data];

  results.reduce((prevRefs, currentFile) => {
    const split = currentFile.reference.split('/');
    // Keep track of app previous references to avoid duplicate folders in explorer
    const allRefs: string[] = [...prevRefs];

    split.reduce((previousRef: string, currentRef: string, i: number) => {
      const currentName: string = `${previousRef}${currentRef}`;
      // If current reference is already in the explorer, then skip it
      if (allRefs.includes(`${currentName}/`) || i === 0) return `${currentName}/`;

      const isFile: boolean = i === split.length - 1;

      // Result to display on explorer
      const newFile: FileFolder = {
        ...currentFile,
        name: currentRef,
        type: isFile ? FileFolderType.FILE : FileFolderType.DIRECTORY,
        reference: isFile ? `${currentName}` : `${currentName}/`,
      };

      // Attach to finalData
      finalData = attachChildren(`${previousRef}`, finalData, formatChildren([newFile]), null, true);

      allRefs.push(`${currentName}/`);
      return `${currentName}/`;
    }, '');

    return allRefs;
  }, []);

  // Filter out folders with no files
  const filteredData: FileFolder[] = finalData.filter((folder) => folder.children?.length !== 0);

  return filteredData;
};
